import { useQuery } from "@tanstack/react-query";
import { API_BASE, ApiError } from "@/shared/api";
import type { OgpData } from "@/shared/types/ogp";

export type UseOgpResult = {
  data: OgpData | undefined;
  isLoading: boolean;
  isError: boolean;
};

/**
 * URLをBase64URL形式にエンコードする
 * 非ASCII文字を含むURLにも対応
 */
function encodeUrlToBase64Url(url: string): string {
  // UTF-8エンコードしてからBase64化（非ASCII文字対応）
  const encoded = btoa(unescape(encodeURIComponent(url)));
  return encoded.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

/**
 * OGP データを取得 (§6.5)
 *
 * - API_BASE 経由 ( /ogp-proxy/{base64url} path 形式)
 * - 4xx/5xx と 200 でも data 欠損ケースを OGP_FETCH_FAILED に正規化
 * - server の標準 envelope は使わず生 fetch (200 with empty data 判定が必要なため)
 */
async function fetchOgp(url: string): Promise<OgpData> {
  const requestUrl = `${API_BASE}/ogp-proxy/${encodeUrlToBase64Url(url)}`;
  const res = await fetch(requestUrl, {
    headers: { Accept: "application/json" },
  });

  let body: unknown = null;
  try {
    const text = await res.text();
    body = text.length > 0 ? JSON.parse(text) : null;
  } catch {
    /* JSON parse 失敗は body=null のまま */
  }

  if (!res.ok) {
    throw new ApiError(
      `OGP fetch failed (HTTP ${res.status})`,
      res.status,
      "OGP_FETCH_FAILED",
      body,
    );
  }

  // 200 でも data が空 / 欠損のケースを失敗扱いに正規化
  const ogpData = (body as { data?: unknown } | null)?.data;
  const isEmptyObject =
    !!ogpData &&
    typeof ogpData === "object" &&
    !Array.isArray(ogpData) &&
    Object.keys(ogpData as Record<string, unknown>).length === 0;
  if (!ogpData || isEmptyObject) {
    throw new ApiError(
      "OGP empty response",
      res.status,
      "OGP_FETCH_FAILED",
      body,
    );
  }

  return body as OgpData;
}

export function useOgpProxy(url: string | null): UseOgpResult {
  return useQuery({
    queryKey: ["ogp", url],
    queryFn: (): Promise<OgpData> => {
      if (!url) throw new Error("URL is required");
      return fetchOgp(url);
    },
    enabled: !!url,
    staleTime: 7 * 24 * 60 * 60 * 1000, // 1週間
    retry: 1,
  });
}
